import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import LeaveButton from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/components/LeaveButton'
import { ensureOrbitronFontLoaded } from 'src/utils/loadOnDemandFonts'
import { getMmssString } from '../LiveSessionPageComponents'
import { LSVideoPartHeader, LSVideoPartHeaderButton } from '../LiveSessionPageStyles'
import { LiveSessionHeaderDetails } from './LiveSessionHeaderDetails'
import SessionTopTabs from './TopTabs/SessionTopTabs'

export default function LiveSessionHeaderComponent({
  setTimeElapsedInSeconds,
  timeElapsedInSeconds,
  bookedEventFinalData,
  auxiliaryData,
  userRole,
  selectedPlatform,
  handleLeaveRoom,
  changeLayoutFunction,
  setAnchorElForHeaderMenu,
  anchorElForHeaderMenu,
  setDrawerForMoreOptionsOpenMobile,
  hideEndSessionButton = false,
  isAlreadyMarkedAsCompleted,
  onOpenContentDrawer,
  extraHeaderAction,
  sessionStarted,
  // Tabs props
  menuItemsList,
  selectedMenuItem,
  setSelectedMenuItem,
  actionButtons,
  therapyReportStatus,
  onSettingsClick,
  onOpenDrawer, // Callback to open drawer for secondary sections
}) {
  const [sessionStartTimestamp, setSessionStartTimestamp] = useState(null)
  const [currentElapsedTime, setCurrentElapsedTime] = useState(0)

  useEffect(() => {
    ensureOrbitronFontLoaded()
  }, [])

  // Track when session starts to calculate real-time elapsed time
  useEffect(() => {
    if (sessionStarted && !sessionStartTimestamp) {
      // Store the timestamp when session starts, accounting for any pre-existing elapsed time
      const initialTime = timeElapsedInSeconds || 0
      // Set start timestamp to current time minus the initial elapsed time
      // This ensures continuity if timer was already running
      setSessionStartTimestamp(Date.now() - initialTime * 1000)
      setCurrentElapsedTime(initialTime)
    } else if (!sessionStarted) {
      // Reset when session ends
      setSessionStartTimestamp(null)
      setCurrentElapsedTime(0)
    }
  }, [sessionStarted, sessionStartTimestamp, timeElapsedInSeconds])

  // Calculate real-time elapsed time based on actual start timestamp
  // This ensures the timer doesn't pause when tab is in background
  useEffect(() => {
    if (!sessionStarted || !sessionStartTimestamp) {
      return
    }

    const updateTimer = () => {
      const now = Date.now()
      // Calculate elapsed time from the actual start timestamp
      const elapsed = Math.floor((now - sessionStartTimestamp) / 1000)
      setCurrentElapsedTime(elapsed)
    }

    // Update immediately
    updateTimer()

    // Update every second - but calculation is based on real time, not intervals
    // So even if intervals pause, the calculation will be correct when resumed
    const intervalId = setInterval(updateTimer, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [sessionStarted, sessionStartTimestamp, setTimeElapsedInSeconds])

  async function leaveSession(markAsCompleted = false) {
    if (setTimeElapsedInSeconds) {
      setTimeElapsedInSeconds(currentElapsedTime)
    }
    await handleLeaveRoom(markAsCompleted)
  }

  const isOverDuration = currentElapsedTime > (bookedEventFinalData?.eventDuration * 60 || 0)

  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
      <LSVideoPartHeader>
        <LiveSessionHeaderDetails
          bookedEventFinalData={bookedEventFinalData}
          auxiliaryData={auxiliaryData}
          asHost={userRole === 'mentor'}
        />
        
        {/* Timer: plain time text (no background box) — centred over the bar on desktop, in-flow on
            mobile. Past the booked duration it turns red and blinks instead of sitting on a red box,
            which keeps the header light and leaves room for the patient details. */}
        {sessionStarted && (
          <Box
            sx={{
              position: { xs: 'static', md: 'absolute' },
              left: { md: '50%' },
              transform: { md: 'translateX(-50%)' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mx: { xs: 1, md: 0 },
              zIndex: 10,
              '@keyframes overTimeBlink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.2 } },
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: '"Orbitron", "Roboto Mono", monospace',
                fontSize: { xs: '17px', md: '22px', lg: '24px' },
                fontWeight: 700,
                letterSpacing: { xs: '1.5px', md: '3px' },
                color: isOverDuration ? '#ff4d4f' : '#ffffff',
                textShadow: isOverDuration
                  ? '0 1px 6px rgba(255, 77, 79, 0.55)'
                  : '0 2px 8px rgba(0, 0, 0, 0.3)',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                animation: isOverDuration ? 'overTimeBlink 1s ease-in-out infinite' : 'none',
              }}
            >
              {getMmssString(currentElapsedTime)}
            </Box>
          </Box>
        )}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'right', 
          gap: { xs: 1, md: 1.5 }, 
          alignItems: 'center',
          flexShrink: 0,
        }}>
          {extraHeaderAction}
          {!hideEndSessionButton && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'right',
                gap: '10px',
                flexShrink: 0,
                alignItems: 'center',
              }}
            >
              <LeaveButton
                leaveSession={(markAsCompleted = false) => {
                  leaveSession(markAsCompleted)
                }}
                isAlreadyMarkedAsCompleted={isAlreadyMarkedAsCompleted}
              >
                Leave {displayNamesOfEventTypes?.[bookedEventFinalData?.eventType] || 'Session'}
              </LeaveButton>
            </div>
          )}
        </Box>
      </LSVideoPartHeader>
      
      {/* Tabs integrated into header */}
      {menuItemsList && (
        <SessionTopTabs
          menuItemsList={menuItemsList}
          selectedMenuItem={selectedMenuItem}
          setSelectedMenuItem={setSelectedMenuItem}
          onOpenContentDrawer={onOpenContentDrawer}
          actionButtons={actionButtons}
          therapyReportStatus={therapyReportStatus}
          onSettingsClick={onSettingsClick}
          onOpenDrawer={onOpenDrawer}
        />
      )}
    </Box>
  )
}
